// CreditNova Card Component - Premium Midnight Luxe Design
import * as React from 'react';
import { cn } from '@/lib/utils';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'glass' | 'elevated' | 'interactive';
  glow?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', glow = false, ...props }, ref) => {
    const variants = {
      default: 'bg-card border border-border shadow-premium',
      glass: 'glass',
      elevated: 'glass-elevated',
      interactive: 'card-interactive',
    };

    return (
      <div
        ref={ref}
        className={cn(
          'rounded-xl text-card-foreground',
          variants[variant],
          glow && 'animate-pulse-glow',
          className
        )}
        {...props}
      />
    );
  }
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5 p-6', className)}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'text-2xl font-display font-semibold leading-none tracking-tight text-foreground',
      className
    )}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
));
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center p-6 pt-0', className)}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

// Premium Stat Card Component
interface StatCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  variant?: 'default' | 'gold' | 'emerald' | 'warning' | 'muted';
}

const StatCard = React.forwardRef<HTMLDivElement, StatCardProps>(
  ({ className, title, value, icon, trend, variant = 'default', ...props }, ref) => {
    const iconColors = {
      default: 'bg-gold-500/10 text-gold-400',
      gold: 'bg-gold-500/10 text-gold-400',
      emerald: 'bg-emerald-500/10 text-emerald-400',
      warning: 'bg-warning/10 text-warning',
      muted: 'bg-muted text-muted-foreground',
    };

    return (
      <div
        ref={ref}
        className={cn(
          'glass rounded-xl p-6 stat-card group hover:glass-elevated transition-all duration-300',
          className
        )}
        {...props}
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">{title}</p>
            <p className="text-3xl font-display font-bold text-foreground">{value}</p>
            {trend && (
              <p
                className={cn(
                  'text-sm mt-2 flex items-center gap-1',
                  trend.isPositive ? 'text-emerald-400' : 'text-red-400'
                )}
              >
                <span>{trend.isPositive ? '↑' : '↓'}</span>
                <span>{Math.abs(trend.value)}%</span>
              </p>
            )}
          </div>
          {icon && (
            <div
              className={cn(
                'p-3 rounded-xl transition-transform group-hover:scale-110',
                iconColors[variant]
              )}
            >
              {icon}
            </div>
          )}
        </div>
      </div>
    );
  }
);
StatCard.displayName = 'StatCard';

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  StatCard,
};
